import React, { useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check } from 'lucide-react';
import { Button } from './ui/button';

// Custom dark monochrome theme based on oneDark
const monoTheme = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: '#111114',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    background: '#111114',
    fontSize: '13px',
  }
};

function CodeBlock({ language, value }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <div className="code-block-wrapper rounded-lg overflow-hidden my-3 border border-white/[0.06]">
      <div className="flex items-center justify-between px-3 py-1.5 bg-white/[0.03] border-b border-white/[0.06]">
        <span className="text-xs text-white/40 font-mono">{language || 'code'}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="copy-btn h-6 px-2 text-white/40 hover:text-white/70 hover:bg-white/[0.06]"
          data-testid="message-copy-code-button"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span className="ml-1 text-xs">{copied ? 'Copiato' : 'Copia'}</span>
        </Button>
      </div>
      <SyntaxHighlighter
        style={monoTheme}
        language={language || 'text'}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: '1rem',
          background: '#111114',
          borderRadius: 0
        }}
        wrapLongLines
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
}

export default function MarkdownRenderer({ content }) {
  const components = useMemo(() => ({
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const value = String(children).replace(/\n$/, '');
      
      if (!inline && (match || value.includes('\n'))) {
        return <CodeBlock language={match?.[1]} value={value} />;
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  }), []);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
}
